import { useEffect, useRef } from 'react';

const HeroSection = () => {
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let frame: number;
    const onScroll = () => {
      frame = requestAnimationFrame(() => {
        if (imgRef.current) {
          const y = window.scrollY * 0.28;
          imgRef.current.style.transform = `scale(1.12) translateY(${y}px)`;
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden bg-[#1A1612]">
      {/* Parallax background — real site image */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          ref={imgRef}
          className="absolute inset-0 transition-none hero-zoom"
          style={{ transform: 'scale(1.12) translateY(0px)', willChange: 'transform' }}
        >
          <img
            src="https://images.squarespace-cdn.com/content/v1/6205743531e70503b4cb51e0/1728142634188-758IT1T9ESRJHEOOJ2NJ/A+Madd+Tea+Party.jpg"
            alt="Original storybook artwork by Deb's Art Designs"
            className="w-full h-full object-cover object-top"
          />
        </div>
        {/* Vibrant overlays — minimal, let the artwork breathe */}
        <div className="absolute inset-0 bg-[#1A1612]/25"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1612]/55 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1612]/50 via-transparent to-[#1A1612]/10"></div>
        {/* Grain */}
        <div className="absolute inset-0 grain-overlay"></div>
      </div>

      {/* Left vertical accent line */}
      <div className="absolute left-8 md:left-16 top-0 bottom-0 flex-col items-center justify-center hidden lg:flex">
        <div className="w-px flex-1 bg-gradient-to-b from-transparent via-white/10 to-transparent"></div>
        <div className="py-8">
          <p className="text-white/20 text-[9px] uppercase tracking-[0.5em] font-light" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            Est. 2015 &nbsp;&bull;&nbsp; Pylesville, MD
          </p>
        </div>
        <div className="w-px flex-1 bg-gradient-to-b from-transparent via-white/10 to-transparent"></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full px-8 md:px-20 lg:px-36 xl:px-48 pt-40 pb-28">
        <div className="max-w-4xl">
          {/* Eyebrow */}
          <div className="flex items-center gap-5 mb-14">
            <div className="w-20 h-px bg-[#C9A96E]/70"></div>
            <span className="text-[#C9A96E]/80 uppercase tracking-[0.55em] text-[9px] font-medium">
              Custom Art Studio
            </span>
          </div>

          {/* H1 */}
          <h1 className="font-serif leading-[0.88] tracking-[-0.01em] mb-12" style={{ fontSize: 'clamp(4rem, 9vw, 8.5rem)', fontWeight: 300 }}>
            <span className="block text-white">Original</span>
            <span className="block text-white">Artwork,</span>
            <span className="block italic text-[#D4B483]" style={{ fontWeight: 300 }}>Created With</span>
            <span className="block text-white">Intention.</span>
          </h1>

          {/* Minimal subtext */}
          <p className="text-white/50 text-sm md:text-base font-light leading-[2] tracking-[0.08em] mb-16 max-w-sm">
            Hand-crafted. Entirely original.<br />Designed around your vision.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <button
              onClick={() => scrollTo('contact')}
              className="whitespace-nowrap px-10 py-4 bg-transparent border border-[#C9A96E]/70 text-[#C9A96E] font-light text-[10px] tracking-[0.45em] uppercase hover:bg-[#C9A96E]/15 hover:border-[#C9A96E] transition-all duration-700 cursor-pointer"
            >
              Commission Artwork
            </button>
            <button
              onClick={() => scrollTo('services')}
              className="whitespace-nowrap px-10 py-4 text-white/50 font-light text-[10px] tracking-[0.45em] uppercase hover:text-white/80 transition-all duration-500 cursor-pointer flex items-center gap-4"
            >
              Enter Gallery
              <div className="w-8 h-px bg-white/30"></div>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom scroll line */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3">
        <div className="w-px h-16 bg-gradient-to-b from-white/30 to-transparent scroll-line"></div>
      </div>
    </section>
  );
};

export default HeroSection;
